<template>
  <div class="pb-5 sm:flex sm:items-center sm:justify-between">
    <h2 class="text-3xl text-vice-teal-900 font-semibold leading-tight tracking-tight">
      Virtual clinics
    </h2>
    <Button v-show="!isMobile" @click="togglePlatformRequirementsModal(true)">
      Check platform requirements
    </Button>
    <SystemCheckModal
      @togglePlatformRequirementsModal="togglePlatformRequirementsModal"
      v-if="isCheckPlatformRequirements"
      :is-clinician="false"
    />
  </div>
  <div>
    <div class="sm:hidden py-2">
      <label for="tabs" class="sr-only">Select a tab</label>
      <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
      <select
        id="tabs"
        name="tabs"
        @change="setTabState($event.target.value)"
        class="
          block
          w-full
          focus:ring-vice-teal-500
          focus:border-vice-teal-500
          border-gray-300
          rounded-md
        "
      >
        <option v-for="tab in tabs" :key="tab.name" :value="tab.id" :selected="tab.current">
          {{ tab.name }}
        </option>
      </select>
    </div>
    <div class="hidden sm:block">
      <nav class="relative z-0 rounded-lg shadow flex divide-x divide-gray-200" aria-label="Tabs">
        <button
          v-for="(tab, tabIdx) in tabs"
          :key="tab.name"
          @click="setTabState(tab.id)"
          :class="[
            tab.current
              ? 'border-vice-teal-400 text-grey-900'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            tab.current ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700',
            tabIdx === 0 ? 'rounded-l-lg step-student-overview' : '',
            tabIdx === tabs.length - 1 ? 'rounded-r-lg step-student-past-sessions' : '',
            `group relative min-w-0 flex-1 overflow-hidden bg-white
             p-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10`,
          ]"
          :aria-current="tab.current ? 'page' : undefined"
        >
          <span>{{ tab.name }}</span>
          <span v-if="tab.count" class="ml-1.5 bg-gray-100 rounded-full px-2.5 pb-0.5">
            <span class="text-xxs font-medium">{{ tab.count }}</span>
          </span>
          <span
            aria-hidden="true"
            :class="[
              tab.current ? 'bg-vice-teal-500' : 'bg-transparent',
              'absolute inset-x-0 bottom-0 h-0.5',
            ]"
          />
        </button>
      </nav>
    </div>
  </div>
  <CourseRequiredAlert
    :show="!$page.props.userFinishedCourse"
    :url="$page.props.baseCourseUrl"
    class="rounded-md p-2 mt-5"
  />

  <div class="grid grid-cols-2 gap-14 pt-11">
    <!-- My clinician sessions -->
    <div
      class="col-span-2 sm:col-span-1 pb-14"
      :class="this.tabs[0]['current'] ? 'block' : 'hidden'"
    >
      <div class="relative flex items-center h-10">
        <div class="absolute inset-0 flex items-center" aria-hidden="true">
          <div class="w-full border-t border-gray-300" />
        </div>
        <div class="relative flex justify-start pb-px">
          <span class="pr-3 pb-px bg-light-grey text-lg font-medium text-vice-teal-700">
            My clinical sessions
          </span>
        </div>
      </div>

      <CardUserSession
        v-for="sessionDetails in this.$page.props.sessions.actual_sessions"
        :key="sessionDetails.session_id"
        :sessionDetails="sessionDetails"
      ></CardUserSession>

      <div class="mt-8 text-gray-500 text-center" v-if="myClinicianSessionsListIsEmpty()">
        Sessions in which you're invited or you'll participate will appear here.
      </div>
    </div>

    <!-- Courses & Past sessions -->
    <div class="col-span-2 sm:col-span-1" :class="this.tabs[0].current ? 'block' : 'hidden'">
      <!-- My courses -->
      <div class="relative pb-14" v-if="this.$page.props.learningCourses.length">
        <!-- Header -->
        <div class="relative h-10">
          <div class="absolute inset-0 flex items-center h-full" aria-hidden="true">
            <div class="w-full border-t border-gray-300" />
          </div>
          <div class="relative flex justify-between items-center h-full">
            <span class="pr-3 pb-px bg-light-grey text-lg font-medium text-vice-teal-700">
              My courses
            </span>
          </div>
        </div>

        <CourseCard
          v-for="course in this.$page.props.learningCourses"
          :key="course.id"
          :courseUuid="course.uuid"
          class="mt-6"
        >
          <template #header>
            {{ course.title }}
          </template>
          <template #content>
            <div class="prose prose-sm prose-vice">
              <!-- Progress bar -->
              <div class="flex w-full bg-vice-teal-50 rounded-full h-2.5">
                <div
                  class="bg-vice-teal-600 h-2.5 rounded-full"
                  :style="{
                    width: `${Math.round(course.progress)}%`,
                  }"
                />
              </div>
            </div>
          </template>
        </CourseCard>

        <!-- No courses -->
        <div
          class="mt-8 text-gray-500 text-center"
          v-if="!Object.keys($page.props.learningCourses).length"
        >
          Your courses will appear here.
        </div>
      </div>

      <!-- Recent past sessions -->
      <div class="relative">
        <div class="relative h-10">
          <div class="absolute inset-0 flex items-center h-full" aria-hidden="true">
            <div class="w-full border-t border-gray-300" />
          </div>
          <div class="relative flex justify-between items-center h-full">
            <span class="pr-3 pb-px bg-light-grey text-lg font-medium text-vice-teal-700">
              Recent past sessions
            </span>
            <div class="flex bg-light-grey pl-3.5">
              <button
                data-cy="open-past-session-table"
                @click="setTabState('past_sessions')"
                type="button"
                class="
                  flex
                  pl-4
                  pr-3.5
                  py-2
                  text-sm
                  bg-white
                  shadow-sm
                  border border-gray-300
                  focus:outline-none
                  focus:ring-gray-500
                  focus:border-gray-500
                  sm:text-sm
                  rounded-md
                "
              >
                <span class="inline-block text-gray-700">See all</span>
                <ChevronRightIcon class="inline-block text-gray-500 w-5 h-5 text-sm" />
              </button>
            </div>
          </div>
        </div>
        <CardUserSession
          v-for="pastSessionDetails in this.$page.props.sessions.past_sessions.slice(0, 3)"
          :key="pastSessionDetails.id"
          :sessionDetails="pastSessionDetails"
          :isPastSession="true"
        ></CardUserSession>

        <div
          class="mt-8 text-gray-500 text-center"
          v-if="!Object.keys($page.props.sessions.past_sessions).length"
        >
          Your past sessions will appear here.
        </div>
      </div>
    </div>
    <div class="col-span-2 sm:col-span-2" :class="this.tabs[0].current ? 'block' : 'hidden'">
      <UpcomingSessions />
    </div>

    <!-- Past sessions -->
    <PastSessions :class="this.tabs[1]['current'] ? 'block' : 'hidden'" />
  </div>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ChevronRightIcon } from '@heroicons/vue/outline';
import UpcomingSessions from './UpcomingSessions.vue';
import DateFormatMixin from '@/Mixins/DateFormatMixin.vue';
import PastSessions from './PastSessions.vue';
import CardUserSession from '@/Components/Cards/Session/CardUserSession.vue';
import CourseRequiredAlert from '@/Components/Alerts/CourseRequiredAlert';
import SystemCheckModal from '@/Components/Modals/SystemCheckModal.vue';
import useTourManager from '@/Composables/useTourManager';
import { userDashboardSteps } from '@/Data/tourSteps';
import { isMobileDevice } from '@/Utils/helpers';
import Button from '@/Jetstream/Button.vue';
import CourseCard from '@/Components/Cards/CourseCard.vue';

const userDashboardTour = useTourManager('userDashboardTour', userDashboardSteps);

export default {
  components: {
    CourseCard,
    ChevronRightIcon,
    CardUserSession,
    PastSessions,
    UpcomingSessions,
    CourseRequiredAlert,
    SystemCheckModal,
    Button,
  },
  mounted() {
    this.humanizeTime();
    setTimeout(() => {
      userDashboardTour.startTour();
    }, 500);

    navigator.mediaDevices.ondevicechange = () => {
      this.togglePlatformRequirementsModal();
    };
  },
  beforeUnmount() {
    navigator.mediaDevices.ondevicechange = null;
  },
  props: {
    sessionInvites: Array,
  },
  mixins: [DateFormatMixin],
  setup() {
    const pageProps = usePage().props.value;
    const myClinicianSessionsListIsEmpty = () => pageProps.sessions.actual_sessions.length === 0;

    return { myClinicianSessionsListIsEmpty };
  },
  data() {
    return {
      actual_sessions: this.$page.props.sessions.actual_sessions,
      isCheckPlatformRequirements: false,
      isMobile: isMobileDevice(),
      tabs: [
        {
          id: 'overview',
          name: 'Overview',
          href: '#',
          current: true,
          count: 0,
        },
        {
          id: 'past_sessions',
          name: 'Past sessions',
          href: '#',
          current: false,
          count: Object.keys(this.$page.props.sessions.past_sessions).length,
        },
      ],
    };
  },
  methods: {
    humanizeTime() {
      const $this = this;
      setInterval(() => {
        for (let i = 0; i < $this.actual_sessions.length; i++) {
          $this.actual_sessions[i].session.details.time_left = $this.getTimeLeft(
            $this.actual_sessions[i].session.details.session_start_by_timezone,
          );
        }
      }, 1000);
    },
    getTimeLeft(startTime) {
      const now = dayjs(); // todays date
      const end = dayjs(startTime); // another date

      dayjs.extend(duration);
      dayjs.extend(relativeTime);

      const durationMoment = dayjs.duration(end.diff(now));
      const seconds = durationMoment.asSeconds();
      if (seconds <= 0) return '';
      return dayjs.duration(seconds, 'seconds').humanize();
    },
    setTabState(id) {
      this.tabs.forEach((tab) => {
        tab.current = tab.id === id;
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    togglePlatformRequirementsModal(show = false) {
      this.isCheckPlatformRequirements = show;
    },
  },
};
</script>
<style scoped>
.min-w-\[200px\] {
  min-width: 200px;
}
</style>
